import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Gift, Copy, Check, UserPlus } from 'lucide-react';
import axios from 'axios';
import Footer from '@/components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReferralPage() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState('');
  const [using, setUsing] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferral();
  }, []);

  const fetchReferral = async () => {
    try {
      const res = await axios.get(`${API}/referral/my`, { headers: getAuthHeader() });
      setReferral(res.data);
    } catch (e) {
      console.error('Could not load referral');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (referral?.code) {
      await navigator.clipboard.writeText(referral.code);
      setCopied(true);
      toast.success('Kod kopierad!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleUseCode = async () => {
    if (!code.trim()) return;
    setUsing(true);
    try {
      await axios.post(`${API}/referral/use`, { code: code.trim() }, { headers: getAuthHeader() });
      toast.success('Kod aktiverad! Din vän får 1 månad gratis.');
      setCode('');
      fetchReferral();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte aktivera koden');
    } finally {
      setUsing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="referral-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-4 flex justify-between items-center">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="rounded-full" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Tillbaka
          </Button>
          <h1 className="font-heading text-xl">Bjud in vänner</h1>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <div className="text-center mb-8 animate-fade-in">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-4">
            <Gift className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h2 className="font-heading text-2xl mb-2">Dela Livon</h2>
          <p className="text-muted-foreground text-sm max-w-sm mx-auto">
            Bjud in en vän och de får en varm välkomst — du får en gratis månad premium!
          </p>
        </div>

        {/* Your code */}
        <div className="bg-card rounded-2xl p-6 border border-border/30 mb-6 animate-fade-in">
          <p className="text-sm font-medium mb-3">Din inbjudningskod</p>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-muted rounded-xl px-4 py-3 font-mono text-lg tracking-wider text-center font-bold">
              {referral?.code || '—'}
            </div>
            <Button
              onClick={handleCopy}
              variant="outline"
              className="rounded-xl h-12 w-12 p-0"
              data-testid="copy-code-btn"
            >
              {copied ? <Check className="w-5 h-5 text-emerald-500" /> : <Copy className="w-5 h-5" />}
            </Button>
          </div>
          <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground">
            <span>Bjudna vänner: {referral?.invited_count || 0}</span>
            <span>Gratismånader tjänade: {referral?.rewarded_months || 0}</span>
          </div>
        </div>

        {/* Use a code */}
        <div className="bg-card rounded-2xl p-6 border border-border/30 animate-fade-in">
          <div className="flex items-center gap-2 mb-3">
            <UserPlus className="w-5 h-5 text-primary" />
            <p className="text-sm font-medium">Har du en kod?</p>
          </div>
          <div className="flex gap-2">
            <Input
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder="Skriv in kod..."
              className="rounded-xl font-mono tracking-wider"
              data-testid="referral-code-input"
            />
            <Button
              onClick={handleUseCode}
              disabled={using || !code.trim()}
              className="rounded-xl"
              data-testid="use-code-btn"
            >
              {using ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aktivera'}
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
